<template>
  <div>
    <va-modal
      id="appointmentFormModal"
      class="evollu-form"
      :size="modalSize"
      :value="showModal"
      :noOutsideDismiss="true"
      :okText="$t('modals.buttons.save')"
      :cancelText="$t('modals.buttons.cancel')"
      :dangerText="$t('modals.buttons.delete')"
      :showDelete="selectedAppointment != undefined"
      :okDisabled="isSubmitting || !canSubmitForm"
      @ok="submit"
      @cancel="cancel"
      @remove="remove"
    >

      <div slot="header" class="modal-title">
        {{modalTitle}}
      </div>

      <div class="row no-gutters">
        <div
          :class="{
            'col-12 col-md-8': hasAppointments,
            'col-12': !hasAppointments}"
          >

          <!-- Section Title -->
          <div class="col-12">
            <label class="form-label section-label"> {{ $t('agenda.appointments.appointment_section') }}</label>
          </div>

          <div class="row">
            <!-- Appointment Start -->
            <div class="col-6">
              <div
                class="form-group"
                :class="{
                  'has-error': formErrors.has('start')}"
                >
                <div class="input-group">
                  <DatePicker
                    id="appointment-start"
                    class="w-100"
                    v-model="form.start"
                    :config="datePickerConfig"
                    :placeholder="$t('agenda.availability.start')"
                    :error="formErrors.has('start')"
                    :error-messages="[$t(formErrors.get('start'))]"
                    @input="formErrors.clear('start')" />
                </div>
              </div>
            </div>

            <!-- Appointment End -->
            <div class="col-6">
              <div
                class="form-group"
                :class="{'has-error': formErrors.has('end')}"
              >
                <div class="input-group">
                  <DatePicker
                    id="appointment-end"
                    class="w-100"
                    v-model="form.end"
                    :config="datePickerConfig"
                    :placeholder="$t('agenda.availability.end')"
                    :error="formErrors.has('end')"
                    :error-messages="[$t(formErrors.get('end'))]"
                    @input="formErrors.clear('end')" />

                </div>
              </div>
            </div>

            <!-- Appointment Type -->
            <div class="col-12" v-if="appointmentTypes.length > 1">
              <div>
                <div class="form-group">
                  <div class="input-group">
                    <multiselect
                      v-model="form.appointmentType"
                      :options="appointmentTypes"
                      track-by="id"
                      label="name"
                      select-label=""
                      selected-label=""
                      deselect-label=""
                      :placeholder="$t('agenda.appointments.appointment_type')"
                      :allow-empty="true"
                      :multiple="false"
                      :searchable="true"
                      >
                      <span slot="noResult">{{'patients.noResults'| translate}}</span>
                    </multiselect>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="row">

            <!-- Section Title -->
            <div class="col-12">
              <label class="form-label section-label">{{ $t('agenda.appointments.person_section') }}</label>
            </div>

            <!-- Person Name -->
            <div class="col-12">
              <div class="form-group">
                <va-input
                  type="text"
                  v-model="form.person.name"
                  :placeholder="$t('agenda.appointments.name')"
                  :error="formErrors.has('person.name') || invalidName"
                  :error-messages="[formErrors.has('person.name') ? $t(formErrors.get('person.name')) : $t('agenda.appointments.errors.the_person_name_must_have_at_least_2_characters')]" />
              </div>
            </div>

            <!-- Person Phone -->
            <div class="col-12">
              <div class="form-group">
                <div class="input-group">
                  <vue-tel-input
                    v-model="form.person.phone"
                    class="w-100"
                    maxlength="20"
                    :preferredCountries="$store.state.countries.allowed"
                    :disabledFetchingCountry="true"
                    :defaultCountry="defaultCountrySelection"
                    :placeholder="$t('references.inputs.phone')"
                    @input="onEditingPhoneInput">
                  </vue-tel-input>
                </div>
                <small v-show="formErrors.has('person.phone')" class="help text-danger pl-2" style="font-size: 0.8rem;">
                  {{'references.errors.invalidPhone' | translate}}
                </small>
              </div>
            </div>

            <!-- Person Email -->
            <div class="col-12">
              <div class="form-group">
                <va-input
                  type="text"
                  v-model="form.person.email"
                  :placeholder="'Email'"
                  :error="formErrors.has('person.email') || invalidEmail"
                  :error-messages="[formErrors.has('person.email') ? $t(formErrors.get('person.email')) : $t('agenda.appointments.errors.the_person_email_must_be_a_valid_email_address')]" />
              </div>
            </div>

            <!-- Person City -->
            <div class="col-12">
              <div class="form-group">
                <va-input
                  type="text"
                  v-model="form.person.city"
                  :placeholder="$t('agenda.appointments.city')" />
              </div>
            </div>
          </div>

          <div class="row">

            <!-- Section Title -->
            <div class="col-12">
              <label class="form-label section-label">{{$t('agenda.inputs.observations')}}</label>
            </div>

            <!-- Observations input -->
            <div class="col-12">
              <div class="form-group">
                <va-input
                  v-model="form.observations"
                  type="textarea"
                  :minRows="3"
                  :maxRows="8" />
              </div>
            </div>
          </div>

          <div class="row mt-2">
            <div class="col-12">
              <va-checkbox
                :label="$t('agenda.appointments.share_data')"
                :id="'checkbox-user-data'"
                :isCircle="true"
                v-model="sharePersonalDataPermission"/>
            </div>
          </div>

        </div>

        <AppointmentsList
          class="col-12 col-md-4"
          v-if="hasAppointments"
          :screening="availability"
          :id="selectedAppointment ? selectedAppointment.id : null"
          :appointments="availability.appointments"
          />
      </div>
    </va-modal>

    <ConfirmationModal
      v-if="showDeletingModal"
      :title="$t('agenda.appointments.delete')"
      :body="$t('agenda.appointments.delete_message')"
      @modal-confirmed="destroyAppointment"
      @modal-canceled="cancelDestroyAppointment" />
  </div>
</template>
<script>
import DatePicker from '@/components/common/DatePicker'
import moment from 'moment'
import { VueTelInput } from 'vue-tel-input'
import Multiselect from 'vue-multiselect'
import AppointmentsList from './AppointmentsList'
import Errors from '@/utils/StandardErrors.js'
import { AppointmentService } from '@/api/BookingService'
import ConfirmationModal from '@/components/common/ConfirmationModal'
import { EMAIL_REGEX } from '@/utils/regex'

const errorsPrefix = 'agenda.appointments.validationMessages.'

export default {
  components: {
    DatePicker,
    VueTelInput,
    Multiselect,
    AppointmentsList,
    ConfirmationModal
  },
  props: {
    fromEvent: {
      type: Object,
      required: false
    },
    selectedAppointment: {
      type: Object,
      required: false
    },
    availability: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      isSubmitting: false,
      showModal: false,
      showDeletingModal: false,
      vueTelInputRedrawIndex: 0,
      canCancel: true,
      show: true,
      sharePersonalDataPermission: false,
      validPhoneNumber: true,
      form: {
        start: null,
        end: null,
        person: {
          name: null,
          phone: '',
          email: null,
          city: null
        },
        observations: null,
        appointmentType: null,
        availability_id: this.availability.id
      },
      formErrors: new Errors(),
      appointmentTypes: []
    }
  },
  created () {
    this.loadAppointmentTypes()
    this.isUpdating ? this.fillForm() : this.preFillForm()
  },
  mounted () {
    this.showModal = true
  },
  methods: {
    onEditingPhoneInput: function (value, options) {
      if (!options.valid) {
        this.validPhoneNumber = false
        this.formErrors.record({ 'person.phone': ['The person.phone field is invalid.'] }, errorsPrefix)
      } else if (this.formErrors.has('person.phone')) {
        this.validPhoneNumber = true
        this.formErrors.clear('person.phone')
      }
    },
    fillForm: function () {
      this.form = JSON.parse(JSON.stringify(this.selectedAppointment))
    },
    preFillForm: function () {
      if (this.fromEvent) {
        this.form.start = this.fromEvent.start
        this.form.end = this.fromEvent.end
      }
    },
    cancel: function () {
      if (this.canCancel) {
        setTimeout(() => {
          this.$emit('cancel')
        }, 500)
      }
    },
    remove: function () {
      this.showModal = false
      this.showDeletingModal = true
    },
    submit: function () {
      let data = this.getFormData()
      this.isUpdating ? this.update(data) : this.create(data)
    },
    getFormData: function () {
      let body = JSON.parse(JSON.stringify(this.form))
      body.start = this.$options.filters.utcTime(body.start)
      body.end = this.$options.filters.utcTime(body.end)
      if (body.appointmentType) {
        body.appointment_type_id = body.appointmentType.id
        delete body.appointmentType
        delete body.type
      }
      if (body.facility) {
        delete body.facility
      }
      if (body.user) {
        delete body.user
      }
      if (body.owner) {
        delete body.owner
      }
      if (body.status) {
        body.status = body.status.id
      }
      return body
    },
    create (data) {
      this.isSubmitting = true
      AppointmentService.store(data)
        .then(appointment => {
          this.showToast(this.$t('agenda.appointments.actions.created'), this.$store.state.successToastOptions)
          this.$emit('appointment-created', { appointment })
        }).catch(errors => {
          console.log(errors)
          if (errors.response.status === 422) {
            this.formErrors.record(errors.response.data.errors, errorsPrefix)
          }
        }).finally(() => {
          this.isSubmitting = false
        })
    },
    update (data) {
      this.isSubmitting = true
      AppointmentService.update(this.selectedAppointment.id, data)
        .then(appointment => {
          this.showToast(this.$t('agenda.appointments.actions.updated'), this.$store.state.successToastOptions)
          this.$emit('appointment-updated', { appointment })
        }).catch(errors => {
          if (errors.response.status === 422) {
            this.formErrors.record(errors.response.data.errors, errorsPrefix)
          }
        }).finally(() => {
          this.isSubmitting = false
        })
    },
    destroyAppointment: function () {
      AppointmentService.delete(this.selectedAppointment.id)
        .then(() => {
          this.showToast(this.$t('agenda.appointments.actions.deleted'), this.$store.state.successToastOptions)
          this.$emit('appointment-deleted')
        }).catch(errors => {
          console.log(errors)
        })
    },
    cancelDestroyAppointment: function () {
      this.showDeletingModal = false
      this.showModal = true
    },
    loadAppointmentTypes () {
      this.$http({
        url: this.$store.state.ebooking_server + '/appointment-types',
        params: { service: this.availability.activity.id }
      }).then((response) => {
        this.appointmentTypes = response.data.data
      }).catch((error) => {
        console.log(error)
      })
    },
    onCampaignAdded: function ({ appointments }) {
      this.$emit('campaign-added', { availability: this.availability, appointments })
    },
  },
  computed: {
    modalTitle: function () {
      if (this.selectedAppointment) {
        return `${this.$t('agenda.appointments.edit')} - ${this.titleDate}`
      }
      return `${this.$t('agenda.appointments.create')} - ${this.titleDate}`
    },
    canSubmitForm: function () {
      return this.form.person.name !== null && this.form.person.name.trim() !== '' && this.form.person.phone && this.validPhoneNumber && this.form.appointmentType !== null && !this.invalidEmail && !this.invalidName && this.sharePersonalDataPermission
    },
    hasAppointments: function () {
      return this.availability.appointments !== null && this.availability.appointments.length
      // return !!(this.availability.appointments && this.availability.appointments.length)
    },
    modalSize: function () {
      return this.hasAppointments ? 'large' : 'medium'
    },
    datePickerConfig: function () {
      return {
        enableTime: true,
        minTime: moment(this.availability.start).format('HH:mm:ss'),
        maxTime: moment(this.availability.end).format('HH:mm:ss'),
        time_24hr: true,
        minuteIncrement: 15,
        noCalendar: false,
        dateFormat: 'Y-m-d H:i:S'
      }
    },
    isUpdating: function () {
      return this.selectedAppointment !== undefined
    },
    selectedLanguage: function () {
      return this.$i18n.locale()
    },
    titleDate: function () {
      return this.availability ? this.moment(this.availability.start, 'YYYY-MM-DD').locale(this.selectedLanguage).format('dddd, LL') : null
    },
    invalidEmail: function () {
      if (this.form.person.email) {
        return !this.form.person.email.match(EMAIL_REGEX)
      }
      return false
    },
    invalidName: function () {
      if (this.form.person.name) {
        return this.form.person.name.length < 2
      }
      return false
    },
    authUserEntity: function () {
      return this.$auth.user().entity
    },
    defaultCountrySelection: function () {
      if (this.authUserEntity && this.authUserEntity.country) {
        return this.authUserEntity.country
      }
      return this.$store.state.countries.default
    },
  },
  watch: {
    selectedLanguage: function () {
      this.vueTelInputRedrawIndex++
    },
    appointmentTypes: {
      handler: function (appointmentTypes) {
        if (appointmentTypes.length) {
          this.form.appointmentType = appointmentTypes[0]
        }
      },
      deep: true
    }
  },
  filters: {
    timeFormat: function (value) {
      return moment(value, 'YYYY-MM-DD HH:mm:ss').format('HH:mm')
    }
  }
}
</script>
